import { useCallback, useEffect, useState } from 'react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import {
  deleteModerationTarget,
  getModeratorProfile,
  hideModerationTarget,
  listModerationActionsForModerator,
  listReportsForModerator,
  updateReportReview,
} from '../services/moderationService';

const reportStatuses = ['open', 'reviewing', 'resolved', 'dismissed'];

function Moderation() {
  const { currentUser } = useAuth();
  const [moderator, setModerator] = useState(null);
  const [reports, setReports] = useState([]);
  const [actions, setActions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [resolutions, setResolutions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadModeration = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const loadedModerator = await getModeratorProfile(currentUser.uid);
      setModerator(loadedModerator?.active ? loadedModerator : null);
      if (loadedModerator?.active) {
        const [loadedReports, loadedActions] = await Promise.all([
          listReportsForModerator(),
          listModerationActionsForModerator(),
        ]);
        setReports(loadedReports);
        setActions(loadedActions);
      } else {
        setReports([]);
        setActions([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadModeration();
  }, [loadModeration]);

  async function handleUpdate(report, status) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await updateReportReview(report, status, resolutions[report.id] || report.resolution || '', currentUser.uid);
      setMessage(`Report marked ${status}.`);
      await loadModeration();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleHideTarget(report) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const note = resolutions[report.id] || report.resolution || 'Target visibility set to private by moderator.';
      await hideModerationTarget(report, currentUser.uid, note);
      await updateReportReview(
        report,
        'resolved',
        note,
        currentUser.uid
      );
      setMessage('Target hidden and report marked resolved.');
      await loadModeration();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteTarget(report) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const note = resolutions[report.id] || report.resolution || 'Reported comment/message deleted by moderator.';
      await deleteModerationTarget(report, currentUser.uid, note);
      await updateReportReview(
        report,
        'resolved',
        note,
        currentUser.uid
      );
      setMessage('Target deleted and report marked resolved.');
      await loadModeration();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function canHideTarget(report) {
    return ['problemDoc', 'pageEntry', 'profile'].includes(report.targetType);
  }

  function canDeleteTarget(report) {
    return ['problemComment', 'pageComment', 'problemMessage'].includes(report.targetType);
  }

  const visibleReports = reports.filter((report) => statusFilter === 'all' || report.status === statusFilter);

  return (
    <>
      <SEO title="Moderation" />
      <section className="page-heading">
        <p className="eyebrow">Moderation</p>
        <h1>Review reports before Problems grows wider.</h1>
        <p>Moderator-only tools for triaging reported problem documents, comments, messages, profiles, and page entries.</p>
      </section>
      {error && <p className="error-text">{error}</p>}
      {message && <p className="notice">{message}</p>}
      {loading ? <div className="surface-card">Loading moderation access...</div> : null}
      {!loading && !moderator ? (
        <section className="surface-card">
          <h2>Moderator access required</h2>
          <p className="muted">Reports are intentionally hidden from normal users. Add an active `moderators/{currentUser.uid}` document to enable this dashboard.</p>
        </section>
      ) : null}
      {!loading && moderator ? (
        <>
          <section className="surface-card">
            <div className="section-toolbar">
              <div>
                <h2>Reports</h2>
                <p className="muted">Update status and leave a short resolution note. Reports are retained for audit history.</p>
              </div>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="all">All statuses</option>
                {reportStatuses.map((status) => <option value={status} key={status}>{status}</option>)}
              </select>
            </div>
            {visibleReports.length === 0 ? <p className="muted">No reports match this filter.</p> : null}
            <div className="item-list moderation-report-list">
              {visibleReports.map((report) => (
                <article className="content-item moderation-report" key={report.id}>
                  <p className="tag">{report.targetType} · {report.status}</p>
                  <h3>{report.targetId}</h3>
                  <p><strong>Reason:</strong> {report.reason}</p>
                  <p className="muted">Reporter: {report.reporterId}</p>
                  {report.moderatorId ? <p className="muted">Last moderator: {report.moderatorId}</p> : null}
                  <label>Resolution note
                    <textarea rows="3" value={resolutions[report.id] ?? report.resolution ?? ''} onChange={(event) => setResolutions((current) => ({ ...current, [report.id]: event.target.value }))} />
                  </label>
                  <div className="button-row compact-row">
                    {reportStatuses.map((status) => (
                      <button className="button button-secondary button-small" type="button" disabled={saving} key={status} onClick={() => handleUpdate(report, status)}>{status}</button>
                    ))}
                  </div>
                  <div className="moderation-target-actions">
                    <p className="tag">Target action</p>
                    {canHideTarget(report) ? (
                      <button className="button button-secondary button-small" type="button" disabled={saving} onClick={() => handleHideTarget(report)}>Hide target</button>
                    ) : null}
                    {canDeleteTarget(report) ? (
                      <button className="button button-secondary button-small" type="button" disabled={saving} onClick={() => handleDeleteTarget(report)}>Delete target</button>
                    ) : null}
                    {!canHideTarget(report) && !canDeleteTarget(report) ? <p className="muted">No direct target action for this report type yet.</p> : null}
                  </div>
                </article>
              ))}
            </div>
          </section>
          <section className="surface-card moderation-audit-card">
            <div className="section-toolbar">
              <div>
                <h2>Audit Trail</h2>
                <p className="muted">Immutable records of moderator status and target actions.</p>
              </div>
            </div>
            {actions.length === 0 ? <p className="muted">No moderation actions recorded yet.</p> : null}
            <div className="item-list moderation-action-list">
              {actions.slice(0, 30).map((action) => (
                <article className="content-item moderation-action" key={action.id}>
                  <p className="tag">{action.action} · {action.targetType}</p>
                  <h3>{action.targetId}</h3>
                  <p className="muted">Report: {action.reportId || 'none'} · Moderator: {action.moderatorId}</p>
                  {action.note ? <p>{action.note}</p> : null}
                </article>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </>
  );
}

export default Moderation;
